import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import TreeView from '@Services/TreeView';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { Pill } from '@els/els-react--pill';
import RemovablePill from '@Components/RemovablePill';
import allowedTags from '@Services/AllowedTag';
import useBreakpoint from '@Hooks/useBreakpoint';
import { ALLOWED_TAGS_ARRAY, SELECTED_PANEL_HEADING_ID } from '../TreeViewConstants';
import { INLINE_TREE_ITEM_SELECTOR, TREE_CONTROL_SELECTOR, focusSiblingOrTree, focusTreeAfterClear } from '../treeFocus';

import './TreeViewSelected.scss';

const CONTAINER_SELECTOR = 'treeViewSelected-container';
const SELECTED_ITEM_BUTTON_SELECTOR = '.treeViewSelected__list_item button';

function TreeViewSelected({
  selectedTaxonomies = [],
  selectedCustomItems = [],
  selectedListExpanded,
  onToggle,
  onRemove,
  onClear,
  layout,
}) {
  const [isMoreVisible, setIsMoreVisible] = useState(false);
  // true  → custom list rendered first (a custom item was the last thing added)
  // false → taxonomy list rendered first (default)
  const [customFirst, setCustomFirst] = useState(false);

  // Derive stable primitives from the array contents so change detection works
  // even when a parent mutates an array in-place (same reference, different
  // contents) — a pattern that fools React's own reference-equality dep check.
  //
  // taxMaxOrder  – highest selectionOrder stamp in the taxonomy list; changes
  //               whenever a taxonomy is selected or re-selected regardless of
  //               whether the array reference changed.
  // customLastItem – last element of the custom list; together with length this
  //               catches both appends and removals of custom items.
  const taxLength = selectedTaxonomies.length;
  const taxMaxOrder = selectedTaxonomies.reduce((max, t) => Math.max(max, t?.selectionOrder ?? 0), 0);
  const customLength = selectedCustomItems.length;
  const customLastItem = selectedCustomItems[customLength - 1];

  // Refs hold the previous render's primitive values for comparison inside the
  // effect.  Initialised to the first-render values so the initial mount is
  // treated as "nothing changed yet" (both lists start equal to themselves).
  const prevTaxLengthRef = useRef(taxLength);
  const prevTaxMaxOrderRef = useRef(taxMaxOrder);
  const prevCustomLengthRef = useRef(customLength);
  const prevCustomLastItemRef = useRef(customLastItem);

  function getIsMoreVisible() {
    const container = document.getElementById(CONTAINER_SELECTOR);

    if (!container) {
      return false;
    }

    const containerWidth = container.offsetWidth;

    const isWidthAcceptable = TreeView.isElementsWidthMoreThenMaximal(container.children, containerWidth);

    return selectedListExpanded || isWidthAcceptable;
  }

  useEffect(() => {
    const taxonomiesChanged = taxLength !== prevTaxLengthRef.current || taxMaxOrder !== prevTaxMaxOrderRef.current;

    const customChanged = customLength !== prevCustomLengthRef.current || customLastItem !== prevCustomLastItemRef.current;

    prevTaxLengthRef.current = taxLength;
    prevTaxMaxOrderRef.current = taxMaxOrder;
    prevCustomLengthRef.current = customLength;
    prevCustomLastItemRef.current = customLastItem;

    if (selectedTaxonomies || selectedCustomItems) {
      setIsMoreVisible(getIsMoreVisible());
    }

    // Only flip the order when exactly one list changes so that an unrelated
    // re-render doesn't reset the ordering.
    if (customChanged && !taxonomiesChanged) {
      setCustomFirst(true);
    } else if (taxonomiesChanged && !customChanged) {
      setCustomFirst(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxLength, taxMaxOrder, customLength, customLastItem]);

  const toggleExpanded = () => {
    onToggle(!selectedListExpanded);
  };

  const isTabletUp = useBreakpoint('tablet');

  const isSidebarLayout = layout === 'sidebar';

  // Sidebar panel (Taxonomy tree): always expanded above mobile — the toggle
  // behavior below only applies to the mobile chevron button.
  const isSidebarPanelExpanded = isTabletUp || Boolean(selectedListExpanded);

  const sidebarHeadingRef = useRef(null);
  const sidebarToggleFocusedRef = useRef(false);
  const wasTabletUpRef = useRef(isTabletUp);

  // Crossing into tablet+ (resize/rotate) unmounts the mobile toggle button in
  // favor of plain text. If it held focus, that focus would otherwise drop to
  // <body> — move it to the heading instead. Checking document.activeElement
  // here would race the unmount (jsdom/browsers clear it back to <body> as
  // soon as the focused node leaves the DOM, before this effect runs), so
  // focus state is captured via onFocus/onBlur instead.
  useEffect(() => {
    if (isTabletUp && !wasTabletUpRef.current && sidebarToggleFocusedRef.current) {
      sidebarHeadingRef.current?.focus();
    }
    wasTabletUpRef.current = isTabletUp;
  }, [isTabletUp]);

  // The sidebar renders the Leyden tree; the inline layout renders the legacy
  // treeitem list. Either is the fallback target once a selection is gone.
  const treeSelector = isSidebarLayout ? TREE_CONTROL_SELECTOR : INLINE_TREE_ITEM_SELECTOR;

  // Clearing removes every pill (and, in the inline layout, the button that was
  // clicked), so move focus to the first tree control once the list re-renders.
  const handleClear = () => {
    onClear();
    focusTreeAfterClear(treeSelector);
  };

  const getAllowedTags = (value) => allowedTags(value, ALLOWED_TAGS_ARRAY);

  const getMoreIcon = () => (selectedListExpanded ? 'Minus' : 'Plus');

  const selectedItemCount = selectedTaxonomies.length + selectedCustomItems.length;

  // Focuses the previous item in the combined list, or the next if there is no
  // previous, falling back to the first tree control when the removed item was
  // the last selected value. See @Containers/TreeView/treeFocus for why the
  // target is resolved up front and re-asserted on the next frame.
  function focusPrevOrNextItem(currentIndex) {
    focusSiblingOrTree({ itemSelector: SELECTED_ITEM_BUTTON_SELECTOR, currentIndex, treeSelector });
  }

  return (
    <div
      className={classnames('treeViewSelected', {
        'treeViewSelected-expanded': selectedListExpanded,
        'treeViewSelected-sidebar': layout === 'sidebar',
      })}>
      {isSidebarLayout ? (
        <div className="treeViewSelected__title treeViewSelected__title-sidebar">
          {/*
            The pill shows a bare number, which is meaningless read on its own, so
            the digits are hidden from assistive tech and replaced by a phrase. The
            heading keeps its static text so the panel's landmark name does not
            change on every selection.
          */}
          <Pill className="treeViewSelected__count">
            <span aria-hidden="true">{String(selectedItemCount)}</span>
            <span className="u-els-hide-visually">{`${selectedItemCount} selected`}</span>
          </Pill>
          <h4
            id={SELECTED_PANEL_HEADING_ID}
            ref={sidebarHeadingRef}
            tabIndex={-1}
            className="u-els-font-size-base u-els-line-height-default">
            {isTabletUp ? (
              <span data-e2e="tree-view-selected-heading-text">Selected search items</span>
            ) : (
              <ButtonWithIcon
                id="treeViewSelected-heading-toggle"
                data-e2e="tree-view-selected-heading-toggle"
                type="borderless"
                size="small"
                sprite={isSidebarPanelExpanded ? 'ChevronUp' : 'ChevronDown'}
                iconRight
                onClick={toggleExpanded}
                onFocus={() => {
                  sidebarToggleFocusedRef.current = true;
                }}
                onBlur={() => {
                  sidebarToggleFocusedRef.current = false;
                }}
                aria-expanded={isSidebarPanelExpanded}
                aria-controls={CONTAINER_SELECTOR}>
                Selected search items
              </ButtonWithIcon>
            )}
          </h4>
          {onClear && (
            <Button data-e2e="tree-view-selected-clear" type="link" size="small" onClick={handleClear}>
              Clear selected
            </Button>
          )}
        </div>
      ) : (
        <div className="treeViewSelected__title">
          <h4 className="u-els-font-size-base u-els-line-height-default">Selected search items:</h4>
          <div className="treeViewSelected__title_actions">
            {onClear && (
              <Button data-e2e="tree-view-selected-clear" type="borderless" size="small" onClick={handleClear}>
                Clear selected
              </Button>
            )}
            {isMoreVisible && (
              <ButtonWithIcon
                data-e2e="tree-view-selected-showMore"
                type="link"
                size="small"
                linkIconSize="s"
                sprite={getMoreIcon()}
                iconOnly
                onClick={toggleExpanded}
                aria-expanded={Boolean(selectedListExpanded)}
                aria-controls="treeViewSelected-container"
                aria-label={selectedListExpanded ? 'Hide selected search items' : 'Show selected search items'}
                className="e2e-tree-view-selected-showMore">
                {selectedListExpanded ? 'Less' : 'More'}
              </ButtonWithIcon>
            )}
          </div>
        </div>
      )}
      <ul id="treeViewSelected-container" className="treeViewSelected__list" hidden={isSidebarLayout && !isSidebarPanelExpanded}>
        {(() => {
          // Render both lists reversed so the newest item is always at the top.
          // The list that changed most recently is rendered first so that item
          // appears above everything else.
          const taxonomyList = selectedTaxonomies
            .slice()
            .reverse()
            .map((taxonomy, reversedIndex) => {
              // DOM position depends on whether custom list is rendered before us.
              const domIndex = customFirst ? selectedCustomItems.length + reversedIndex : reversedIndex;
              return (
                <li className="treeViewSelected__list_item" key={`selected-taxonomies-${taxonomy.id}`}>
                  {isSidebarLayout ? (
                    <RemovablePill
                      className="treeViewSelected__pill"
                      label={taxonomy.name}
                      onRemove={() => {
                        focusPrevOrNextItem(domIndex);
                        onRemove(taxonomy);
                      }}>
                      {getAllowedTags(taxonomy.name)}
                    </RemovablePill>
                  ) : (
                    <>
                      <span className="treeViewSelected__list_item_label" title={taxonomy.name}>
                        {getAllowedTags(taxonomy.name)}
                      </span>
                      <ButtonWithIcon
                        id={`remove-selected-taxonomies-${taxonomy.id}`}
                        data-e2e="tree-view-selected-item-remove"
                        type="link"
                        linkIconSize="xs"
                        sprite="Close"
                        iconOnly
                        onClick={() => {
                          focusPrevOrNextItem(domIndex);
                          onRemove(taxonomy);
                        }}
                        className="e2e-tree-view-selected-item-remove">
                        Remove
                      </ButtonWithIcon>
                    </>
                  )}
                </li>
              );
            });

          const customList = selectedCustomItems
            .slice()
            .reverse()
            .map((custom, reversedIndex) => {
              // Recover original array index so onRemove can identify the item.
              const originalIndex = selectedCustomItems.length - 1 - reversedIndex;
              // DOM position depends on whether taxonomy list is rendered before us.
              const domIndex = customFirst ? reversedIndex : selectedTaxonomies.length + reversedIndex;
              return (
                <li className="treeViewSelected__list_item" key={`selected-taxonomies-${custom}-${originalIndex}`}>
                  {isSidebarLayout ? (
                    <RemovablePill
                      className="treeViewSelected__pill"
                      label={custom}
                      onRemove={() => {
                        focusPrevOrNextItem(domIndex);
                        onRemove({ value: custom, index: originalIndex, custom: true });
                      }}>
                      {getAllowedTags(custom)}
                    </RemovablePill>
                  ) : (
                    <>
                      <span className="treeViewSelected__list_item_label" title={custom}>
                        {getAllowedTags(custom)}
                      </span>
                      <ButtonWithIcon
                        id={`remove-selected-custom-${originalIndex}`}
                        data-e2e="tree-view-selected-item-remove"
                        type="link"
                        size="small"
                        linkIconSize="s"
                        sprite="Close"
                        iconOnly
                        onClick={() => {
                          focusPrevOrNextItem(domIndex);
                          onRemove({ value: custom, index: originalIndex, custom: true });
                        }}
                        className="e2e-tree-view-selected-item-remove">
                        Cancel
                      </ButtonWithIcon>
                    </>
                  )}
                </li>
              );
            });

          // Both groups are keyed. Without keys they are positional siblings, so
          // flipping `customFirst` swaps the two child slots and React reconciles
          // slot-by-slot against a completely different key set — every <li> is
          // unmounted and remounted, which destroys the DOM node we just moved
          // focus to. Keyed fragments make React match by key and keep the nodes.
          const taxonomyGroup = <React.Fragment key="taxonomy-group">{taxonomyList}</React.Fragment>;
          const customGroup = <React.Fragment key="custom-group">{customList}</React.Fragment>;

          return customFirst ? [customGroup, taxonomyGroup] : [taxonomyGroup, customGroup];
        })()}
      </ul>
    </div>
  );
}

TreeViewSelected.propTypes = {
  selectedTaxonomies: PropTypes.array,
  selectedCustomItems: PropTypes.array,
  selectedListExpanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  layout: PropTypes.oneOf(['inline', 'sidebar']),
};

TreeViewSelected.defaultProps = {
  layout: 'inline',
};

export default TreeViewSelected;
